import React, { useState } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  Edit, 
  Copy, 
  Trash2, 
  Eye,
  FileImage,
  Grid3x3,
  List,
  Calendar
} from 'lucide-react';
import templatePreview from '@/assets/template-preview-1.jpg';

const TemplateManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const templates = [
    {
      id: 1,
      name: 'Modern Luxury Flyer',
      category: 'Luxury',
      lastModified: '2024-03-15',
      status: 'Published',
      thumbnail: templatePreview,
      campaigns: 12,
      type: 'Custom'
    },
    {
      id: 2,
      name: 'Family Home Showcase',
      category: 'Residential',
      lastModified: '2024-03-12',
      status: 'Draft',
      thumbnail: templatePreview,
      campaigns: 8,
      type: 'Custom'
    },
    {
      id: 3,
      name: 'Waterfront Property',
      category: 'Waterfront',
      lastModified: '2024-03-10',
      status: 'Published',
      thumbnail: templatePreview,
      campaigns: 15,
      type: 'Premium'
    },
    {
      id: 4,
      name: 'New Construction',
      category: 'Commercial',
      lastModified: '2024-03-08',
      status: 'Published',
      thumbnail: templatePreview,
      campaigns: 6,
      type: 'Standard'
    },
    {
      id: 5,
      name: 'Open House Invitation',
      category: 'Events',
      lastModified: '2024-03-05',
      status: 'Published',
      thumbnail: templatePreview,
      campaigns: 22,
      type: 'Custom'
    },
    {
      id: 6,
      name: 'Market Update Newsletter',
      category: 'Newsletter',
      lastModified: '2024-03-02',
      status: 'Draft',
      thumbnail: templatePreview,
      campaigns: 3,
      type: 'Standard'
    }
  ];

  const getStatusBadge = (status: string) => {
    const variants = {
      'Published': 'bg-cta/10 text-cta',
      'Draft': 'bg-muted text-muted-foreground',
      'Archived': 'bg-destructive/10 text-destructive'
    };
    return variants[status as keyof typeof variants] || 'bg-muted text-muted-foreground';
  };

  const getTypeBadge = (type: string) => {
    const variants = {
      'Custom': 'bg-primary/10 text-primary border-primary/20',
      'Premium': 'bg-cta/10 text-cta border-cta/20',
      'Standard': 'bg-muted text-muted-foreground border-border'
    };
    return variants[type as keyof typeof variants] || 'bg-muted text-muted-foreground border-border';
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <main className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-black">Template Management</h1>
                <p className="text-black mt-1">Manage your saved templates and create new ones</p>
              </div>
              <div className="flex gap-3">
                <Button asChild variant="professional" size="lg">
                  <Link to="/templates">
                    <FileImage className="mr-2 h-4 w-4" />
                    Browse Gallery
                  </Link>
                </Button>
                <Button asChild variant="cta" size="lg">
                  <Link to="/builder">
                    <Plus className="mr-2 h-4 w-4" />
                    Create Template
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="bg-gradient-card shadow-card border-0">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-primary mb-2">{templates.length}</div>
                <div className="text-muted-foreground">Total Templates</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-card shadow-card border-0">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-primary mb-2">
                  {templates.filter(t => t.status === 'Published').length}
                </div>
                <div className="text-muted-foreground">Published</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-card shadow-card border-0">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-primary mb-2">
                  {templates.filter(t => t.type === 'Custom').length}
                </div>
                <div className="text-muted-foreground">Custom Templates</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-card shadow-card border-0">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-primary mb-2">
                  {templates.reduce((sum, t) => sum + t.campaigns, 0)}
                </div>
                <div className="text-muted-foreground">Total Launches</div>
              </CardContent>
            </Card>
          </div>

          {/* Filters and View Toggle */}
          <Card className="shadow-card mb-6">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4 items-center">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      placeholder="Search templates..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="luxury">Luxury</SelectItem>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="waterfront">Waterfront</SelectItem>
                      <SelectItem value="events">Events</SelectItem>
                      <SelectItem value="newsletter">Newsletter</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex border border-border rounded-md">
                    <Button
                      variant={viewMode === 'grid' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                      className="rounded-r-none"
                    >
                      <Grid3x3 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('list')}
                      className="rounded-l-none"
                    >
                      <List className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Templates Grid/List */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
                <Card key={template.id} className="shadow-card group hover:shadow-elevated transition-smooth">
                  <div className="relative">
                    <img
                      src={template.thumbnail}
                      alt={template.name}
                      className="w-full h-48 object-cover rounded-t-lg"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-t-lg">
                      <div className="absolute bottom-4 left-4 right-4 flex gap-2">
                        <Button size="sm" variant="secondary" className="flex-1">
                          <Eye className="h-4 w-4 mr-1" />
                          Preview
                        </Button>
                        <Button asChild size="sm" variant="cta" className="flex-1">
                          <Link to="/builder">
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-primary">{template.name}</h3>
                      <Badge className={getTypeBadge(template.type)}>{template.type}</Badge>
                    </div>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm text-muted-foreground">{template.category}</span>
                      <Badge className={getStatusBadge(template.status)}>{template.status}</Badge>
                    </div>
                    <div className="flex justify-between items-center text-sm text-muted-foreground mb-4">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {template.lastModified}
                      </div>
                      <span>{template.campaigns} Launches</span>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1">
                        <Copy className="h-4 w-4 mr-1" />
                        Duplicate
                      </Button>
                      <Button variant="outline" size="sm">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-black">All Templates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {templates.map((template) => (
                    <div key={template.id} className="flex items-center gap-4 p-4 bg-gradient-card rounded-lg hover:shadow-soft transition-smooth">
                      <img
                        src={template.thumbnail}
                        alt={template.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-primary">{template.name}</h3>
                          <Badge className={getTypeBadge(template.type)}>{template.type}</Badge>
                          <Badge className={getStatusBadge(template.status)}>{template.status}</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{template.category}</span>
                          <span>•</span>
                          <span>Modified {template.lastModified}</span>
                          <span>•</span>
                          <span>{template.campaigns} Launches</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button asChild variant="ghost" size="sm">
                          <Link to="/builder">
                            <Edit className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default TemplateManagement;